import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import api from '../services/api';

export function useMessagesData() {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState('inbox');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const messagesPerPage = 10;

  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);

  const { user: authUser, isAuthenticated, isAdmin: authIsAdmin } = useAuth();
  const isAdmin = authIsAdmin;

  // Load locations and users on mount
  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const loadInitialData = async () => {
      try {
        const locationsRes = await api.get('/locations/', { signal: controller.signal });
        if (isMounted) {
          const data = locationsRes.data;
          if (data.success || Array.isArray(data.locations)) {
            setLocations(data.locations || data.data || []);
          }
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        console.error('Error fetching locations:', err);
        if (isMounted) toast.error('Nie udało się załadować lokalizacji');
      }

      try {
        const usersRes = await api.get('/messages/users', { signal: controller.signal });
        if (isMounted) {
          const data = usersRes.data;
          if (data.success && data.data) {
            const uniqueUsers = [];
            const seen = new Set();
            data.data.forEach(u => {
              if (!seen.has(u.nazwa)) {
                seen.add(u.nazwa);
                uniqueUsers.push({ login: u.nazwa, typ: u.typ, id: u.id });
              }
            });
            setUsers(uniqueUsers);
          }
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        console.error('Error fetching users:', err);
        if (isMounted) toast.error('Nie udało się załadować użytkowników. Sprawdź uprawnienia do modułu wiadomości.');
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedFolder !== 'all') params.append('folder', selectedFolder);
      if (authUser?.login) params.append('user_login', authUser.login);
      if (authUser?.location_id) params.append('location_id', authUser.location_id);
      if (searchQuery) params.append('search', searchQuery);

      const [messagesRes, statsRes] = await Promise.all([
        api.get(`/messages?${params}`),
        api.get(`/messages/stats?user_login=${authUser?.login || ''}`)
      ]);

      const messagesData = messagesRes.data;
      const statsData = statsRes.data;

      if (messagesData.success && messagesData.data) {
        setMessages(messagesData.data);
      } else if (messagesData.messages) {
        setMessages(messagesData.messages);
      }
      if (statsData.success && statsData.data) {
        setStats({
          unread_count: statsData.data.unread || 0,
          received_count: statsData.data.total_inbox || 0,
          sent_count: statsData.data.sent || 0,
          urgent_count: statsData.data.urgent || 0
        });
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFolder, authUser, searchQuery]);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    if (authUser) {
      const loadMessages = async () => {
        try {
          setLoading(true);
          const params = new URLSearchParams();
          if (selectedFolder !== 'all') params.append('folder', selectedFolder);
          if (authUser?.login) params.append('user_login', authUser.login);
          if (authUser?.location_id) params.append('location_id', authUser.location_id);
          if (searchQuery) params.append('search', searchQuery);

          const [messagesRes, statsRes] = await Promise.all([
            api.get(`/messages?${params}`, { signal: controller.signal }),
            api.get(`/messages/stats?user_login=${authUser?.login || ''}`, { signal: controller.signal })
          ]);

          if (!isMounted) return;

          const messagesData = messagesRes.data;
          const statsData = statsRes.data;

          if (messagesData.success && messagesData.data) {
            setMessages(messagesData.data);
          } else if (messagesData.messages) {
            setMessages(messagesData.messages);
          }
          if (statsData.success && statsData.data) {
            setStats({
              unread_count: statsData.data.unread || 0,
              received_count: statsData.data.total_inbox || 0,
              sent_count: statsData.data.sent || 0,
              urgent_count: statsData.data.urgent || 0
            });
          }
        } catch (error) {
          if (error.name === 'AbortError' || error.name === 'CanceledError') return;
          console.error('Error fetching messages:', error);
        } finally {
          if (isMounted) setLoading(false);
        }
      };

      loadMessages();
    } else {
      const timer = setTimeout(() => setLoading(false), 500);
      return () => {
        clearTimeout(timer);
        isMounted = false;
        controller.abort();
      };
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [selectedFolder, authUser, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [selectedFolder]);

  const handleMarkAsRead = async (message) => {
    if (!message.is_read && authUser?.login) {
      try {
        await api.post(`/messages/${message.id}/read`, { user_login: authUser.login });
        fetchMessages();
      } catch (err) {
        console.error('Error marking message as read:', err);
      }
    }
  };

  const handleSendMessage = async (newMessage) => {
    try {
      await api.post('/messages', {
        ...newMessage,
        nadawca_login: authUser?.login || 'System',
        odbiorca_location_id: newMessage.odbiorca_location_id ? parseInt(newMessage.odbiorca_location_id) : null
      });
      fetchMessages();
      return true;
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Blad podczas wysylania wiadomosci');
      return false;
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Czy na pewno chcesz usunac te wiadomosc?')) return false;
    try {
      await api.delete(`/messages/${messageId}`);
      fetchMessages();
      return true;
    } catch (err) {
      console.error('Error deleting message:', err);
      return false;
    }
  };

  const pageCount = Math.max(1, Math.ceil(messages.length / messagesPerPage));
  const paginatedMessages = messages.slice((page - 1) * messagesPerPage, page * messagesPerPage);

  return {
    messages, stats, loading,
    selectedFolder, setSelectedFolder,
    searchQuery, setSearchQuery,
    page, setPage,
    pageCount, paginatedMessages,
    users, locations,
    authUser, isAuthenticated, isAdmin,
    fetchMessages,
    handleMarkAsRead,
    handleSendMessage,
    handleDeleteMessage,
  };
}
